import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MenuItem } from '@mui/material';
import { Badge } from 'react-bootstrap';

import Sidebar from '../../components/sidebar/Sidebar';
import LoadingSpinner from '../../components/LoadingSpinner';
import '../accounts/view/ViewAccountPage.css';
import { MUI_TABLE } from '../../utils/muiTableConfig';
import { Money, Username } from '../../utils/privacyMask';
import {
  describeBalances,
  totalUsd,
  fmtRelative,
  explorerUrl,
  UNSCANNED_NOTE,
  WALLET_STATUS_COLOURS,
} from '../../utils/wallets';

const API = process.env.REACT_APP_API_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/**
 * Every payment wallet, with its last scanned balance.
 *
 * Read-mostly: renaming, creating and re-scanning are here because they are
 * safe and useful from a desk. Sweeping and key reveal deliberately are NOT --
 * those stay in Discord where the staff gate and the audit trail live, and a
 * browser is the worst place to put either.
 */
export default function WalletsPage() {
  const [wallets, setWallets] = useState([]);
  const [accountsMap, setAccountsMap] = useState({});
  const [funded, setFunded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const [walletRes, accRes, fundedRes] = await Promise.all([
        axios.get(`${API}/wallet?limit=1000`, { headers: headers() }),
        axios.get(`${API}/account`, { headers: headers() }).catch(() => ({ data: [] })),
        axios.get(`${API}/wallet/funded`, { headers: headers() }).catch(() => ({ data: null })),
      ]);

      setWallets(Array.isArray(walletRes.data) ? walletRes.data : []);
      setFunded(fundedRes.data);

      const map = {};
      (accRes.data || []).forEach((a) => { map[a._id] = a.username; });
      setAccountsMap(map);
      setError(null);
    } catch (err) {
      // 503 is its own thing: the wallet service is not configured on this
      // box, which is very different from "there are no wallets".
      if (err.response?.status === 503) {
        setError(
          'The wallet service is not configured on the API host '
          + `(${err.response.data?.detail || 'missing WALLET_* keys'}), so no wallets can be read.`
        );
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to load wallets');
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function rename(wallet) {
    // eslint-disable-next-line no-alert
    const next = window.prompt(`Label for wallet #${wallet.index}`, wallet.label || '');
    if (next === null) return;

    try {
      await axios.patch(`${API}/wallet/${wallet.index}/label`, { label: next }, { headers: headers() });
      setNotice(`Wallet #${wallet.index} renamed.`);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not rename that wallet');
    }
  }

  async function rescan(indexes) {
    setBusy(true);
    setNotice(null);
    try {
      const { data } = await axios.post(`${API}/wallet/balances/scan`, { indexes }, { headers: headers() });
      const skipped = data.cooling?.length ? ` ${data.cooling.length} skipped (scanned moments ago).` : '';
      setNotice(`Scanned ${data.scanned} wallet(s).${skipped}`);
      load();
    } catch (err) {
      setError(
        err.response?.status === 503
          ? err.response.data?.error || 'Balance checking is disabled on the API host.'
          : err.response?.data?.error || 'Could not run that scan'
      );
    } finally {
      setBusy(false);
    }
  }

  async function createWallet() {
    // eslint-disable-next-line no-alert
    const label = window.prompt('Label for the new wallet (optional)');
    if (label === null) return;

    try {
      const { data } = await axios.post(`${API}/wallet/create`, { label, purpose: 'manual' }, { headers: headers() });
      setNotice(`Created wallet #${data.wallet.index}.`);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Could not create a wallet');
    }
  }

  const columns = useMemo(() => [
    {
      header: '#', accessorKey: 'index', size: 70,
      Cell: ({ cell }) => <span style={{ fontWeight: 700 }}>{cell.getValue()}</span>,
    },
    {
      header: 'Label', accessorKey: 'label', size: 160,
      Cell: ({ cell }) => (cell.getValue()
        ? <Username value={cell.getValue()} />
        : <span style={{ color: '#64748b' }}>—</span>),
    },
    {
      header: 'Account', accessorKey: 'accountID', size: 150,
      Cell: ({ cell }) => {
        const id = cell.getValue();
        if (!id) return <span style={{ color: '#64748b' }}>unlinked</span>;
        return <Username value={accountsMap[id] || String(id).slice(-6)} />;
      },
    },
    { header: 'Purpose', accessorKey: 'purpose', size: 110 },
    {
      header: 'Status', accessorKey: 'status', size: 130,
      Cell: ({ cell }) => {
        const value = cell.getValue() || 'unassigned';
        return (
          <Badge style={{ background: WALLET_STATUS_COLOURS[value] || '#64748b', fontWeight: 600 }}>
            {value.replace('_', ' ')}
          </Badge>
        );
      },
    },
    {
      header: 'Balances', accessorKey: 'balances', size: 200, enableSorting: false,
      Cell: ({ row }) => (row.original.hasFunds
        ? <span style={{ color: '#34d399', fontWeight: 600 }}>{describeBalances(row.original.balances)}</span>
        : <span style={{ color: '#64748b' }}>—</span>),
    },
    {
      header: 'Value', accessorKey: 'lastBalanceUsd', size: 100,
      Cell: ({ cell }) => <Money value={cell.getValue()} formatter={(v) => `$${(Number(v) || 0).toFixed(2)}`} />,
    },
    {
      header: 'Last scan', accessorKey: 'lastScanAt', size: 120,
      Cell: ({ cell }) => <span style={{ color: '#94a3b8' }}>{fmtRelative(cell.getValue())}</span>,
    },
    {
      header: 'EVM address', accessorKey: 'addresses.eth', size: 220, enableSorting: false,
      Cell: ({ row }) => {
        const address = row.original.addresses?.eth;
        if (!address) return <span style={{ color: '#64748b' }}>—</span>;
        const url = explorerUrl('eth', address);
        return (
          <a href={url} target="_blank" rel="noreferrer" style={{ color: '#818cf8', fontSize: '0.8rem' }}>
            <Username value={`${address.slice(0, 10)}…${address.slice(-6)}`} />
          </a>
        );
      },
    },
  ], [accountsMap]);

  const table = useMaterialReactTable({
    columns,
    data: wallets,
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableFacetedValues: true,
    enableRowActions: true,
    enableGlobalFilter: true,
    initialState: {
      showColumnFilters: false,
      sorting: [{ id: 'index', desc: true }],
      columnPinning: { right: ['mrt-row-actions'] },
    },
    renderRowActionMenuItems: ({ row }) => [
      <MenuItem key="rename" onClick={() => rename(row.original)} sx={{ m: 0, gap: 1 }}>
        <i className="bi bi-pencil-square" style={{ fontSize: '0.9rem', color: '#64748b' }} />Rename
      </MenuItem>,
      <MenuItem key="scan" onClick={() => rescan([row.original.index])} sx={{ m: 0, gap: 1 }}>
        <i className="bi bi-arrow-repeat" style={{ fontSize: '0.9rem', color: '#64748b' }} />Re-scan balances
      </MenuItem>,
    ],
    ...MUI_TABLE,
  });

  if (loading) return <LoadingSpinner />;

  const fundedWallets = wallets.filter((w) => w.hasFunds);

  return (
    <>
      <Sidebar title="Wallets" />
      <div className="view-page">
        {error && (
          <div style={{
            padding: '12px 16px', marginBottom: 20, background: 'rgba(248,113,113,0.08)',
            border: '1px solid rgba(248,113,113,0.2)', borderRadius: 10, color: '#f87171', fontSize: '0.875rem',
          }}>
            <i className="bi bi-exclamation-triangle-fill me-2" />{error}
          </div>
        )}
        {notice && (
          <div style={{
            padding: '12px 16px', marginBottom: 20, background: 'rgba(52,211,153,0.08)',
            border: '1px solid rgba(52,211,153,0.2)', borderRadius: 10, color: '#34d399', fontSize: '0.875rem',
          }}>
            <i className="bi bi-check-circle-fill me-2" />{notice}
          </div>
        )}

        <div className="view-stats">
          <div className="view-stat-card">
            <div className="view-stat-label">Wallets</div>
            <div className="view-stat-value">{wallets.length}</div>
            <i className="bi bi-wallet2 view-stat-icon" />
          </div>
          <div className="view-stat-card">
            <div className="view-stat-label">Holding funds</div>
            <div className="view-stat-value">{fundedWallets.length}</div>
            <i className="bi bi-cash-coin view-stat-icon" />
          </div>
          <div className="view-stat-card">
            <div className="view-stat-label">Total value</div>
            <div className="view-stat-value">
              <Money value={totalUsd(wallets)} formatter={(v) => `$${(Number(v) || 0).toFixed(2)}`} />
            </div>
            <i className="bi bi-graph-up view-stat-icon" />
          </div>
          <div className="view-stat-card">
            <div className="view-stat-label">Last scan</div>
            <div className="view-stat-value" style={{ fontSize: '1.1rem' }}>
              {fmtRelative(funded?.lastScanAt)}
            </div>
            <i className="bi bi-clock-history view-stat-icon" />
          </div>
        </div>

        <div style={{
          padding: '10px 14px', marginBottom: 16, background: 'var(--color-surface-2)',
          border: '1px solid var(--color-border)', borderRadius: 8, color: '#94a3b8', fontSize: '0.8rem',
        }}>
          <i className="bi bi-info-circle me-2" />{UNSCANNED_NOTE}
        </div>

        <div className="view-panel">
          <div className="view-panel-header">
            <h6 className="view-panel-title">
              <i className="bi bi-wallet2" style={{ color: '#06b6d4' }} />All Wallets
            </h6>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className="view-panel-count">{wallets.length}</span>
              <button
                type="button"
                onClick={() => rescan(fundedWallets.slice(0, 25).map((w) => w.index))}
                disabled={busy || !fundedWallets.length}
                style={{
                  padding: '6px 12px', borderRadius: 999, border: '1px solid var(--color-border)',
                  background: 'transparent', color: '#94a3b8', fontSize: '0.8rem',
                  cursor: busy ? 'wait' : 'pointer',
                }}
              >
                <i className="bi bi-arrow-repeat" /> {busy ? 'Scanning…' : 'Re-scan funded'}
              </button>
              <button
                type="button"
                onClick={createWallet}
                style={{
                  padding: '6px 12px', borderRadius: 999, border: 'none',
                  background: '#4F46E5', color: '#fff', fontSize: '0.8rem', cursor: 'pointer',
                }}
              >
                <i className="bi bi-plus-lg" /> New Wallet
              </button>
            </div>
          </div>
          <MaterialReactTable table={table} />
        </div>
      </div>
    </>
  );
}
